import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Map, List } from 'immutable';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import { EProject } from '../components/EProject';
import config from '../config';
import { loadPage, clearError } from '../actions/eproject/main';
import { sortUniverse, loadUniverse, loadSortedUniverse } from '../actions/eproject/universe';

import { openInternalLink } from '../utils/url';
import { showError } from '../utils/MessagePopup';
import { isResearcher } from '../utils/checkPermissions';

import If from '../components/helpers/If';
import { usePopupContext } from '../contexts/PopupContext';

const types = config.values.getIn(['eproject', 'types']);
const universeColumns = config.tables.getIn(['eproject', 'universe', 'columns']);
const DETAILS_POPUP = 'EProjectDetailsPopup';

/**
 * State container component for project page.
 *
 * @param props {Object}.
 * @param projectData {Immutable.Map} Project information.
 * @param users {Immutable.List} User list.
 * @param universe {Immutable.List} Universe list.
 * @param common {Immutable.Map} Common object.
 */
const EProjectContainer = props => {
  const {
    clearError,
    common,
    harvcoTags,
    history,
    loadPage,
    loadSortedUniverse,
    loadUniverse,
    match,
    projectData,
    sortUniverse,
    targetsPagination,
    user,
    users,
  } = props;

  const { openPopup, closePopup, onClosePopup, getPopupName } = usePopupContext();

  const [lazyLoadingIsEnabled, setLazyLoadingIsEnabled] = useState(true);
  // Saved project details shown in the widget while the edit popup is open, so unsaved edits stay hidden.
  const [savedDetails, setSavedDetails] = useState(null);
  const [all, setAll] = useState(0);

  const tableContainerRef = useRef(null);
  const hasMountedRef = useRef(false);

  const userHasAccess = user ? !isResearcher(user.getIn(['roles', 0, 'slug'], null)) : false;

  const handleTableRef = useCallback(tableContainer => {
    tableContainerRef.current = tableContainer;
  }, []);

  const handleEditEnd = useCallback(() => {
    setSavedDetails(null);
  }, []);

  const reopenDetailsPopup = useCallback(() => {
    openPopup(DETAILS_POPUP, { onClose: handleEditEnd });
  }, [openPopup, handleEditEnd]);

  const showErrorsPopup = useCallback(() => {
    const errors = common.get('errors');
    const isDetailsPopupOpen = getPopupName() === DETAILS_POPUP;

    if (errors && errors.size > 0) {
      const handleErrorClose = () => {
        clearError();
        if (isDetailsPopupOpen) {
          reopenDetailsPopup();
        } else {
          closePopup();
        }
      };

      showError(openPopup, errors, handleErrorClose);
      if (isDetailsPopupOpen) {
        onClosePopup(reopenDetailsPopup);
      }
    }
  }, [common, getPopupName, clearError, closePopup, openPopup, onClosePopup, reopenDetailsPopup]);

  const handleScroll = useCallback(() => {
    const tableContainer = tableContainerRef.current;

    if (!tableContainer) return;

    const rows = tableContainer.getElementsByTagName('tr');
    const lastRow = rows[rows.length - 1];

    if (!lastRow) return;

    if (lastRow.getBoundingClientRect().bottom <= window.innerHeight && lazyLoadingIsEnabled) {
      const currentPage = targetsPagination.get('currentPage');
      const { targetSortField, targetSortDirection } = common.toJS();
      const transformedField = targetSortField?.replace(/([A-Z])/g, letter => `_${letter.toLowerCase()}`);
      const sort = targetSortDirection === 'down' ? `-${transformedField}` : transformedField;

      setLazyLoadingIsEnabled(false);
      if (currentPage < targetsPagination.get('totalPages')) {
        loadUniverse(
          match.params.projectId,
          {
            sort: targetSortField ? sort : null,
            page: currentPage + 1,
            all,
          },
          {
            afterSuccess: () => {
              setLazyLoadingIsEnabled(true);
            },
          },
        );
      }
    }
  }, [lazyLoadingIsEnabled, all, targetsPagination, common, match, loadUniverse]);

  const handleRowDoubleClick = useCallback(({ row, type }) => {
    if (type === types.get('HARVCO_TAGS')) {
      openInternalLink(`/contact/${row.get('id')}/executive`);
    }
  }, []);

  const handleSort = useCallback(
    (event, { type, direction, field }) => {
      if (type === types.get('HARVCO_TAGS')) {
        const { projectId } = match.params;
        const { currentPage, perPage } = targetsPagination.toJS();
        const transformedField = field.replace(/([A-Z])/g, letter => `_${letter.toLowerCase()}`);
        const sort = direction === 'down' ? `-${transformedField}` : transformedField;
        const limit = currentPage * perPage;

        loadSortedUniverse(projectId, { sort, limit, all });
        sortUniverse(field, direction);
      }
    },
    [match, targetsPagination, all, loadSortedUniverse, sortUniverse],
  );

  const handleReload = useCallback(() => {
    const { projectId } = match.params;

    sortUniverse(null, null);
    loadPage(
      projectId,
      {
        afterSuccess: () => {
          setLazyLoadingIsEnabled(true);
        },
      },
      { isReload: true, all },
    );
  }, [match, sortUniverse, loadPage, all]);

  const handleEdit = useCallback(() => {
    setSavedDetails({ projectData, users, harvcoTags, common });
    reopenDetailsPopup();
  }, [projectData, users, harvcoTags, common, reopenDetailsPopup]);

  const handleCeoProjectUniverseChange = useCallback(
    ({ target }) => {
      const { projectId } = match.params;

      setAll(target.value);
      sortUniverse(null, null);
      loadUniverse(
        projectId,
        {
          page: 1,
          all: target.value,
        },
        {
          afterSuccess: () => {
            setLazyLoadingIsEnabled(true);
          },
        },
        true,
      );
    },
    [match, sortUniverse, loadUniverse],
  );

  useEffect(() => {
    const { projectId } = match.params;

    if (userHasAccess) {
      loadPage(projectId, {}, { all });
    } else {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });
    }
    // Mount-only: matches the original componentDidMount exactly.
  }, []);

  // Re-subscribing whenever handleScroll's own deps change (instead of attaching once via a
  // ref-captured value) keeps the listener always calling a fresh, non-stale handleScroll.
  // Capture phase catches scrolls of the layout content container (scroll doesn't bubble).
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  useEffect(() => {
    // componentDidUpdate never fires on mount; the default `errors` state is non-empty
    // (see src/reducers/eproject/common.js), so skipping the first run is required, not optional.
    if (hasMountedRef.current) {
      showErrorsPopup();
    } else {
      hasMountedRef.current = true;
    }
  }, [showErrorsPopup]);

  return (
    <div className="full-height">
      <Helmet
        title={`${(savedDetails?.projectData ?? projectData).getIn(['category', 'name'], '')} [E Project Page]`.trim()}
      />
      <If value={userHasAccess}>
        <EProject
          {...props}
          onCeoProjectUniverseChange={handleCeoProjectUniverseChange}
          onEdit={handleEdit}
          onReload={handleReload}
          onRowDoubleClick={handleRowDoubleClick}
          onSort={handleSort}
          onTableRef={handleTableRef}
          savedDetails={savedDetails}
          universeColumns={universeColumns}
        />
      </If>
    </div>
  );
};

EProjectContainer.propTypes = {
  clearError: PropTypes.func.isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  harvcoTags: PropTypes.instanceOf(List).isRequired,
  loadPage: PropTypes.func.isRequired,
  loadSortedUniverse: PropTypes.func.isRequired,
  loadUniverse: PropTypes.func.isRequired,
  projectData: PropTypes.instanceOf(Map).isRequired,
  sortUniverse: PropTypes.func.isRequired,
  universe: PropTypes.instanceOf(List).isRequired,
  user: PropTypes.instanceOf(Map),
  users: PropTypes.instanceOf(List).isRequired,
};

const mapStateToProps = state => {
  const { universe, ...rest } = state.eproject;

  return {
    ...rest,
    universe: universe.get ? universe.get('data') : universe.data,
    targetsPagination: universe.get ? universe.get('pagination') : universe.pagination,
    user: state.auth.get('user', null),
  };
};

export { EProjectContainer };
export default connect(
  mapStateToProps,
  {
    loadUniverse,
    loadSortedUniverse,
    loadPage,
    sortUniverse,
    clearError,
  },
  mergeProps,
  connectOptions,
)(memo(EProjectContainer));
